import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, MousePointerClick, Timer, Layers } from "lucide-react";
import { getSocialAnalytics } from "@/lib/api/rss-admin.functions";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/social-analytics",
)({
  component: SocialAnalyticsPage,
});

function SocialAnalyticsPage() {
  const [days, setDays] = useState(14);
  const fn = useServerFn(getSocialAnalytics);
  const { data, isLoading } = useQuery({
    queryKey: ["social-analytics", days],
    queryFn: () => fn({ data: { days } }),
  });

  function Bar({
    label,
    value,
    max,
  }: {
    label: string;
    value: number;
    max: number;
  }) {
    const pct = max ? Math.max(2, Math.round((value / max) * 100)) : 0;
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-[11px] text-foreground/80">
          <span className="truncate pr-2">{label}</span>
          <span className="font-semibold text-navy">{value}</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-navy"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  }

  const totals = data?.totals;
  const maxSource = Math.max(1, ...(data?.bySource ?? []).map(([, v]) => v));
  const maxCampaign = Math.max(
    1,
    ...(data?.byCampaign ?? []).map(([, v]) => v),
  );
  const maxDay = Math.max(1, ...(data?.byDay ?? []).map(([, v]) => v));

  return (
    <div className="container-narrow space-y-6 py-10">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">
            Admin · Analytics
          </p>
          <h1 className="mt-1 font-display text-3xl text-navy">
            Social share analytics
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Click attribution and average time-on-page for UTM-tagged shares.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-full border border-border bg-card px-3 py-1.5 text-xs"
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </header>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Crunching numbers…
        </div>
      ) : (
        <>
          <section className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                <MousePointerClick className="h-3.5 w-3.5" /> Total clicks
              </p>
              <p className="mt-1 font-display text-3xl text-navy">
                {totals?.clicks ?? 0}
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                <Layers className="h-3.5 w-3.5" /> Items with clicks
              </p>
              <p className="mt-1 font-display text-3xl text-navy">
                {totals?.uniqueItems ?? 0}
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                <Timer className="h-3.5 w-3.5" /> Avg time on page
              </p>
              <p className="mt-1 font-display text-3xl text-navy">
                {totals?.avgDwellSec ?? 0}s
              </p>
            </div>
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <h2 className="mb-3 font-display text-lg text-navy">By source</h2>
              <div className="space-y-2">
                {(data?.bySource ?? []).slice(0, 10).map(([k, v]) => (
                  <Bar key={k} label={k} value={v} max={maxSource} />
                ))}
                {(data?.bySource ?? []).length === 0 && (
                  <p className="text-xs text-muted-foreground">No clicks yet.</p>
                )}
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <h2 className="mb-3 font-display text-lg text-navy">
                By campaign
              </h2>
              <div className="space-y-2">
                {(data?.byCampaign ?? []).slice(0, 10).map(([k, v]) => (
                  <Bar key={k} label={k} value={v} max={maxCampaign} />
                ))}
                {(data?.byCampaign ?? []).length === 0 && (
                  <p className="text-xs text-muted-foreground">No clicks yet.</p>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-4 shadow-soft">
            <h2 className="mb-3 font-display text-lg text-navy">
              Daily clicks
            </h2>
            <div className="space-y-2">
              {(data?.byDay ?? []).map(([k, v]) => (
                <Bar key={k} label={k} value={v} max={maxDay} />
              ))}
              {(data?.byDay ?? []).length === 0 && (
                <p className="text-xs text-muted-foreground">No clicks yet.</p>
              )}
            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-4 shadow-soft">
            <h2 className="mb-3 font-display text-lg text-navy">
              Top performing items
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3">Kind</th>
                    <th className="py-2 pr-3">Slug / ID</th>
                    <th className="py-2 pr-3 text-right">Clicks</th>
                    <th className="py-2 pr-3 text-right">Avg dwell</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.topItems ?? []).map((it: any) => (
                    <tr
                      key={`${it.kind}-${it.id ?? it.slug}`}
                      className="border-t border-border/60"
                    >
                      <td className="py-2 pr-3 capitalize">{it.kind}</td>
                      <td className="py-2 pr-3 truncate text-foreground/80">
                        {it.slug ?? it.id}
                      </td>
                      <td className="py-2 pr-3 text-right font-semibold text-navy">
                        {it.clicks}
                      </td>
                      <td className="py-2 pr-3 text-right text-muted-foreground">
                        {it.avgDwellSec}s
                      </td>
                    </tr>
                  ))}
                  {(data?.topItems ?? []).length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className="py-3 text-center text-muted-foreground"
                      >
                        No data yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
